import { createRoot } from 'react-dom/client'
import { Photo } from '@cya-routes/shared'
import mapboxgl from 'mapbox-gl'
import { Camera } from 'lucide-react'
import Supercluster from 'supercluster'

export class PhotoRenderer {
  private map: any
  private renderedPhotos = new Set<string>()
  private onPhotoClick?: (photo: Photo) => void
  private onPhotoUpdate?: (id: string, coordinates: { lat: number, lng: number }) => void
  private supercluster: Supercluster
  private currentZoom: number = 10
  private zoomUpdateTimeout: number | null = null
  private selectedPhotoId?: string
  private currentPhotos: Photo[] = []
  private activeHoverPanels = new Set<HTMLDivElement>()
  private hoverTimeouts = new Map<string, NodeJS.Timeout>()
  private moveHandlers = new Map<string, any>()
  private zoomHandler: any = null
  private moveEndHandler: any = null

  constructor(
    map: any, 
    onPhotoClick?: (photo: Photo) => void,
    onPhotoUpdate?: (id: string, coordinates: { lat: number, lng: number }) => void
  ) {
    if (!map || typeof map.getContainer !== 'function') {
      throw new Error('PhotoRenderer requires a valid Mapbox GL Map instance')
    }
    this.map = map
    this.onPhotoClick = onPhotoClick
    this.onPhotoUpdate = onPhotoUpdate
    
    // Initialize supercluster with same settings as POIRenderer
    this.supercluster = new Supercluster({
      radius: 40,
      maxZoom: 20, // Increased from 16 to handle higher zoom levels
      minZoom: 0,
      minPoints: 2
    })
    
    // Store current zoom level
    this.currentZoom = Math.floor(this.map.getZoom())
    
    // Listen for zoom end to update clusters
    this.zoomHandler = () => {
      const newZoom = Math.floor(this.map.getZoom())
      this.currentZoom = newZoom
      this.updateClusters()
    }
    this.map.on('zoomend', this.zoomHandler)
    
    // Listen for moveend to update clusters after panning (with debounce)
    let moveTimeout: number | null = null
    this.moveEndHandler = () => {
      if (moveTimeout) clearTimeout(moveTimeout)
      moveTimeout = setTimeout(() => {
        this.updateClusters()
      }, 100)
    }
    this.map.on('moveend', this.moveEndHandler)
  }

  public renderPhotos(photos: Photo[], selectedPhotoId?: string) {
    this.selectedPhotoId = selectedPhotoId
    this.currentPhotos = photos // Store current photos
    
    // Filter out photos without coordinates
    const validPhotos = photos.filter(photo => 
      photo && 
      photo.coordinates && 
      typeof photo.coordinates.lat === 'number' && 
      typeof photo.coordinates.lng === 'number' &&
      !isNaN(photo.coordinates.lat) && 
      !isNaN(photo.coordinates.lng)
    )
    
    
    if (validPhotos.length === 0) {
      console.log('🎨 No valid photos to render, clearing all markers')
      this.clearAllMarkers()
      return
    }
    
    const features = validPhotos.map(photo => ({
      type: 'Feature' as const,
      properties: {
        photo,
        cluster: false,
        id: photo.id
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [photo.coordinates!.lng, photo.coordinates!.lat]
      }
    }))
    
    try {
      // Load features into supercluster
      this.supercluster.load(features)
      
      // Clear existing markers
      this.clearAllMarkers()
      
      // Update clusters for current zoom
      this.updateClusters()
    } catch (error) {
      console.error('🚨 Supercluster error:', error)
      console.error('Photo data causing error:', validPhotos)
      // Fallback: render photos individually without clustering
      this.renderPhotosWithoutClustering(validPhotos)
    }
  }
  
  private updateClusters() {
    try {
      // Check if photos are hidden (empty array means hidden)
      if (this.currentPhotos.length === 0) {
        console.log('🎨 Photos are hidden, skipping cluster update')
        this.clearAllMarkers()
        return
      }
      
      // Check if supercluster has been loaded with data
      if (!this.supercluster || !this.supercluster.points || this.supercluster.points.length === 0) {
        console.log('🎨 No photos loaded in supercluster, skipping cluster update')
        return
      }
      
      // Get map bounds with padding to ensure markers at edges are included
      const bounds = this.map.getBounds()
      
      // Add padding to bounds (20% of the viewport on each side)
      const west = bounds.getWest()
      const east = bounds.getEast()
      const south = bounds.getSouth()
      const north = bounds.getNorth()
      
      const lngPadding = (east - west) * 0.2
      const latPadding = (north - south) * 0.2
      
      const bbox = [
        west - lngPadding,
        south - latPadding,
        east + lngPadding,
        north + latPadding
      ] as [number, number, number, number]
      
      // Get clusters for current zoom level (use actual zoom, not floored)
      const actualZoom = this.map.getZoom()
      const clusters = this.supercluster.getClusters(bbox, actualZoom)
      
      // Track which markers should exist
      const newMarkerIds = new Set<string>()
      
      clusters.forEach(cluster => {
        if (cluster.properties.cluster) {
          const clusterId = `cluster_${cluster.properties.cluster_id}`
          newMarkerIds.add(clusterId)
          
          // Only render if not already exists
          if (!this.map._photoMarkers?.has(clusterId)) {
            this.renderCluster(cluster)
          }
        } else {
          const photo = cluster.properties.photo
          newMarkerIds.add(photo.id)
          
          // Only render if not already exists
          if (!this.renderedPhotos.has(photo.id)) {
            this.renderPhoto(photo)
          }
        }
      })
      
      // Remove markers that are no longer in view or should be clustered
      if (this.map._photoMarkers) {
        const markersToRemove: string[] = []
        this.map._photoMarkers.forEach((marker: any, id: string) => {
          if (!newMarkerIds.has(id)) {
            markersToRemove.push(id)
          }
        })
        
        markersToRemove.forEach(id => {
          const marker = this.map._photoMarkers.get(id)
          if (marker) {
            marker.remove()
            this.map._photoMarkers.delete(id)
            if (!id.startsWith('cluster_')) {
              this.renderedPhotos.delete(id)
            }
          }
        })
      }
    } catch (error) {
      console.error('🚨 Error updating clusters:', error)
    }
  }

  private renderPhoto(photo: Photo) {
    // Skip if photo is already rendered
    if (this.renderedPhotos.has(photo.id)) {
      console.log('⏭️ Photo already rendered, skipping:', photo.fileName || photo.id)
      return
    }

    this.renderedPhotos.add(photo.id)

    const markerElement = document.createElement('div')
    markerElement.className = 'photo-marker'
    
    const root = createRoot(markerElement)
    root.render(
      <div 
        className="relative drop-shadow-lg" 
        style={{ 
          cursor: this.onPhotoUpdate ? 'move' : 'pointer',
          width: '21px',
          height: '21px'
        }}
      >
        {photo.thumbnailUrl || photo.url ? (
          <div 
            className="w-[21px] h-[21px] rounded-md border border-white overflow-hidden"
            style={{
              backgroundImage: `url(${photo.thumbnailUrl || photo.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              boxShadow: this.selectedPhotoId === photo.id 
                ? '0 0 20px 4px rgba(16, 185, 129, 0.6), 0 0 8px 2px rgba(16, 185, 129, 0.8)' 
                : '0 2px 4px rgba(0,0,0,0.3)'
            }}
          />
        ) : (
          <div 
            className="w-[21px] h-[21px] rounded-md border border-white flex items-center justify-center"
            style={{
              backgroundColor: '#10b981',
              boxShadow: this.selectedPhotoId === photo.id 
                ? '0 0 20px 4px rgba(16, 185, 129, 0.6), 0 0 8px 2px rgba(16, 185, 129, 0.8)' 
                : '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            <Camera className="w-2.5 h-2.5 text-white" strokeWidth={2} />
          </div>
        )}
      </div>
    )

    let clickTimeout: number | null = null
    let hasBeenDragged = false

    markerElement.addEventListener('mousedown', () => {
      hasBeenDragged = false
    })

    markerElement.addEventListener('click', (e) => {
      e.stopPropagation()
      
      // Small delay to ensure this is a click, not the end of a drag
      if (clickTimeout) clearTimeout(clickTimeout)
      clickTimeout = setTimeout(() => {
        if (!hasBeenDragged) {
          console.log('🎯 Photo clicked:', photo.fileName || photo.id)
          if (this.onPhotoClick) {
            this.onPhotoClick(photo)
          }
        }
      }, 100)
    })

    const marker = new mapboxgl.Marker(markerElement, { 
      draggable: !!this.onPhotoUpdate,
      anchor: 'center'
    })
      .setLngLat([photo.coordinates!.lng, photo.coordinates!.lat])
      .addTo(this.map)

    // Only add drag handlers if dragging is enabled
    if (this.onPhotoUpdate) {
      marker.on('dragstart', () => {
        hasBeenDragged = true
      })

      marker.on('dragend', () => {
        const lngLat = marker.getLngLat()
        console.log('🔄 Photo dragged:', photo.fileName || photo.id, 'to', lngLat)
        this.onPhotoUpdate(photo.id, { lat: lngLat.lat, lng: lngLat.lng })
      })
    }

    // Create hover panel for single photo
    let hoverPanel: HTMLDivElement | null = null
    const photoHoverKey = `photo_${photo.id}`
    
    const createHoverPanel = () => {
      // Clear any existing timeout for this photo
      const existingTimeout = this.hoverTimeouts.get(photoHoverKey)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
        this.hoverTimeouts.delete(photoHoverKey)
      }
      
      // Remove existing panel if any
      if (hoverPanel) {
        this.activeHoverPanels.delete(hoverPanel)
        hoverPanel.remove()
      }
      
      hoverPanel = document.createElement('div')
      hoverPanel.className = 'photo-hover-panel'
      hoverPanel.style.cssText = `
        position: fixed;
        background: rgba(31, 41, 55, 0.98);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        padding: 4px;
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
        z-index: 10000;
        display: none;
        pointer-events: auto;
        transition: none;
      `
      
      // Add to tracking set
      this.activeHoverPanels.add(hoverPanel)
      
      const panelRoot = createRoot(hoverPanel)
      panelRoot.render(
        <div className="cursor-pointer hover:scale-105 transition-transform">
          {photo.thumbnailUrl || photo.url ? (
            <div 
              className="w-20 h-20 rounded-lg overflow-hidden border-2 border-white shadow-lg"
              style={{
                backgroundImage: `url(${photo.thumbnailUrl || photo.url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
              onClick={(e) => {
                e.stopPropagation()
                console.log('🎯 Photo clicked from hover panel:', photo.fileName || photo.id)
                if (this.onPhotoClick) {
                  this.onPhotoClick(photo)
                }
                if (hoverPanel) hoverPanel.style.display = 'none'
              }}
              title={photo.caption || photo.fileName || 'Photo'}
            />
          ) : (
            <div 
              className="w-20 h-20 rounded-lg flex items-center justify-center border-2 border-white shadow-lg"
              style={{ backgroundColor: '#10b981' }}
              onClick={(e) => {
                e.stopPropagation()
                console.log('🎯 Photo clicked from hover panel:', photo.fileName || photo.id)
                if (this.onPhotoClick) {
                  this.onPhotoClick(photo)
                }
                if (hoverPanel) hoverPanel.style.display = 'none'
              }}
            >
              <Camera className="w-12 h-12 text-white" strokeWidth={1.5} />
            </div>
          )}
        </div>
      )
      
      document.body.appendChild(hoverPanel)
      
      // Keep panel visible when hovering over it
      hoverPanel.addEventListener('mouseenter', () => {
        const existingTimeout = this.hoverTimeouts.get(photoHoverKey)
        if (existingTimeout) {
          clearTimeout(existingTimeout)
          this.hoverTimeouts.delete(photoHoverKey)
        }
      })
      
      hoverPanel.addEventListener('mouseleave', () => {
        const hideTimeout = setTimeout(() => {
          if (hoverPanel) {
            hoverPanel.style.display = 'none'
            this.hoverTimeouts.delete(photoHoverKey)
          }
        }, 100)
        this.hoverTimeouts.set(photoHoverKey, hideTimeout)
      })
    }
    
    // Hover handlers
    markerElement.addEventListener('mouseenter', (e) => {
      
      // Clear any existing timeout for this photo
      const existingTimeout = this.hoverTimeouts.get(photoHoverKey)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
        this.hoverTimeouts.delete(photoHoverKey)
      }
      
      if (!hoverPanel) {
        createHoverPanel()
      }
      
      if (hoverPanel) {
        // Get the marker's position on screen
        const markerPoint = this.map.project([photo.coordinates!.lng, photo.coordinates!.lat])
        const mapContainer = this.map.getContainer()
        const mapRect = mapContainer.getBoundingClientRect()
        
        // Calculate screen position
        const screenX = mapRect.left + markerPoint.x
        const screenY = mapRect.top + markerPoint.y
        
        // Panel dimensions
        const panelWidth = 88 // 80px image + 8px padding
        const panelHeight = 88
        
        // Position above the marker center
        let left = screenX - panelWidth / 2
        let top = screenY - 10.5 - panelHeight - 10 // 10.5px is half marker height (21px/2), 10px gap
        
        // Keep panel within viewport
        if (left < 10) left = 10
        if (left + panelWidth > window.innerWidth - 10) left = window.innerWidth - panelWidth - 10
        if (top < 10) {
          // Show below if no room above
          top = screenY + 10.5 + 10
        }
        
        hoverPanel.style.left = `${left}px`
        hoverPanel.style.top = `${top}px`
        hoverPanel.style.display = 'block'
      }
    })
    
    markerElement.addEventListener('mouseleave', () => {
      const hideTimeout = setTimeout(() => {
        if (hoverPanel) {
          hoverPanel.style.display = 'none'
          this.hoverTimeouts.delete(photoHoverKey)
        }
      }, 150)
      this.hoverTimeouts.set(photoHoverKey, hideTimeout)
    })

    // Create hover panel immediately (but hidden)
    createHoverPanel()
    
    this.map._photoMarkers = this.map._photoMarkers || new Map()
    this.map._photoMarkers.set(photo.id, marker)
    
    // Store hover panel reference for cleanup
    ;(marker as any)._hoverPanel = hoverPanel

  }
  
  private renderCluster(cluster: any) {
    const [lng, lat] = cluster.geometry.coordinates
    const count = cluster.properties.point_count
    
    // Get all photos in this cluster
    const clusterPhotos = this.supercluster.getLeaves(cluster.properties.cluster_id, Infinity)
      .map((leaf: any) => leaf.properties.photo)
    
    // Check if the selected photo is in this cluster
    const containsSelectedPhoto = this.selectedPhotoId && 
      clusterPhotos.some((photo: Photo) => photo.id === this.selectedPhotoId)
    
    // Use the first photo as the representative
    const representativePhoto = clusterPhotos[0]
    
    const markerElement = document.createElement('div')
    markerElement.className = 'photo-cluster-marker'
    
    const root = createRoot(markerElement)
    root.render(
      <div className="relative cursor-pointer drop-shadow-lg">
        {/* Main photo marker */}
        <div className="relative" style={{ width: '21px', height: '21px' }}>
          {representativePhoto.thumbnailUrl || representativePhoto.url ? (
            <div 
              className="w-[21px] h-[21px] rounded-md border border-white overflow-hidden"
              style={{
                backgroundImage: `url(${representativePhoto.thumbnailUrl || representativePhoto.url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                boxShadow: containsSelectedPhoto 
                  ? '0 0 20px 4px rgba(16, 185, 129, 0.6), 0 0 8px 2px rgba(16, 185, 129, 0.8)' 
                  : '0 2px 4px rgba(0,0,0,0.3)'
              }}
            />
          ) : (
            <div 
              className="w-[21px] h-[21px] rounded-md border border-white flex items-center justify-center"
              style={{
                backgroundColor: '#10b981',
                boxShadow: containsSelectedPhoto 
                  ? '0 0 20px 4px rgba(16, 185, 129, 0.6), 0 0 8px 2px rgba(16, 185, 129, 0.8)' 
                  : '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              <Camera className="w-2.5 h-2.5 text-white" strokeWidth={2} />
            </div>
          )}
        </div>
        
        {/* Count chip - exactly like POI renderer */}
        {count > 1 && (
          <div className="absolute bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-medium z-20" style={{ fontSize: '10px', top: '-6px', right: '-6px' }}>
            +{count - 1}
          </div>
        )}
      </div>
    )

    // Create marker
    const marker = new mapboxgl.Marker(markerElement, { 
      anchor: 'center'
    })
      .setLngLat([lng, lat])
      .addTo(this.map)
    
    // Create floating panel for hover
    let hoverPanel: HTMLDivElement | null = null
    const clusterHoverKey = `cluster_${cluster.properties.cluster_id}`
    
    const createHoverPanel = () => {
      // Clear any existing timeout for this cluster
      const existingTimeout = this.hoverTimeouts.get(clusterHoverKey)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
        this.hoverTimeouts.delete(clusterHoverKey)
      }
      
      // Remove existing panel if any
      if (hoverPanel) {
        this.activeHoverPanels.delete(hoverPanel)
        hoverPanel.remove()
      }
      
      hoverPanel = document.createElement('div')
      hoverPanel.className = 'photo-cluster-hover-panel'
      hoverPanel.style.cssText = `
        position: fixed;
        background: rgba(31, 41, 55, 0.98);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        padding: 4px;
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
        z-index: 10000;
        display: none;
        pointer-events: auto;
        transition: none;
      `
      
      // Add to tracking set
      this.activeHoverPanels.add(hoverPanel)
      
      const panelRoot = createRoot(hoverPanel)
      panelRoot.render(
        <div className="flex flex-row gap-2">
          {clusterPhotos.slice(0, 3).map((photo: Photo) => {
            return (
              <div
                key={photo.id}
                className="cursor-pointer hover:scale-105 transition-transform"
                onClick={(e) => {
                  e.stopPropagation()
                  console.log('🎯 Photo clicked from cluster:', photo.fileName || photo.id)
                  if (this.onPhotoClick) {
                    this.onPhotoClick(photo)
                  }
                  if (hoverPanel) hoverPanel.style.display = 'none'
                }}
                title={photo.caption || photo.fileName || 'Photo'}
              >
                {photo.thumbnailUrl || photo.url ? (
                  <div 
                    className="w-20 h-20 rounded-lg overflow-hidden border-2 border-white shadow-lg"
                    style={{
                      backgroundImage: `url(${photo.thumbnailUrl || photo.url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                ) : (
                  <div 
                    className="w-20 h-20 rounded-lg flex items-center justify-center border-2 border-white shadow-lg"
                    style={{ backgroundColor: '#10b981' }}
                  >
                    <Camera className="w-12 h-12 text-white" strokeWidth={1.5} />
                  </div>
                )}
              </div>
            )
          })}
          {count > 3 && (
            <div className="flex items-center justify-center">
              <div className="w-20 h-20 rounded-lg bg-gray-600 flex items-center justify-center shadow-lg">
                <span className="text-2xl text-white font-bold">+{count - 3}</span>
              </div>
            </div>
          )}
        </div>
      )
      
      document.body.appendChild(hoverPanel)
      
      // Keep panel visible when hovering over it
      hoverPanel.addEventListener('mouseenter', () => {
        const existingTimeout = this.hoverTimeouts.get(clusterHoverKey)
        if (existingTimeout) {
          clearTimeout(existingTimeout)
          this.hoverTimeouts.delete(clusterHoverKey)
        }
      })
      
      hoverPanel.addEventListener('mouseleave', () => {
        const hideTimeout = setTimeout(() => {
          if (hoverPanel) {
            hoverPanel.style.display = 'none'
            this.hoverTimeouts.delete(clusterHoverKey)
          }
        }, 100)
        this.hoverTimeouts.set(clusterHoverKey, hideTimeout)
      })
    }
    
    // Function to update panel position
    const updatePanelPosition = () => {
      if (!hoverPanel || hoverPanel.style.display === 'none') return
      
      // Get marker's current position
      const markerPoint = this.map.project([lng, lat])
      const canvas = this.map.getCanvas()
      const canvasRect = canvas.getBoundingClientRect()
      
      // Calculate screen position
      const screenX = canvasRect.left + markerPoint.x
      const screenY = canvasRect.top + markerPoint.y
      
      // Calculate panel width based on number of photos shown
      const photosShown = Math.min(clusterPhotos.length, 3)
      const extraWidth = count > 3 ? 1 : 0 // Add space for "+X" indicator
      const totalItems = photosShown + extraWidth
      const photoWidth = 80 // 20 * 4 (w-20 = 5rem = 80px)
      const gap = 8 // gap-2 = 0.5rem = 8px
      const panelPadding = 8 // p-1 = 0.25rem = 4px * 2
      const panelWidth = (photoWidth * totalItems) + (gap * (totalItems - 1)) + panelPadding
      const panelHeight = 96
      
      // Position above the marker center
      let left = Math.round(screenX - panelWidth / 2)
      let top = Math.round(screenY - 10.5 - panelHeight - 10) // 10.5px is half marker height (21px/2)
      
      // Keep within viewport
      const padding = 10
      if (left < padding) left = padding
      if (left + panelWidth > window.innerWidth - padding) left = window.innerWidth - panelWidth - padding
      if (top < padding) {
        top = Math.round(screenY + 10.5 + 10)
      }
      
      hoverPanel.style.left = `${left}px`
      hoverPanel.style.top = `${top}px`
    }
    
    // Update position on map move
    let moveHandler: any = null
    const moveHandlerKey = `cluster_${cluster.properties.cluster_id}_move`
    
    // Hover handlers
    markerElement.addEventListener('mouseenter', (e) => {
      
      // Clear any existing timeout for this cluster
      const existingTimeout = this.hoverTimeouts.get(clusterHoverKey)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
        this.hoverTimeouts.delete(clusterHoverKey)
      }
      
      if (!hoverPanel) {
        createHoverPanel()
      }
      
      if (hoverPanel) {
        hoverPanel.style.display = 'block'
        updatePanelPosition()
        
        // Add map move handler to keep panel aligned
        if (!moveHandler) {
          moveHandler = () => updatePanelPosition()
          this.map.on('move', moveHandler)
          this.moveHandlers.set(moveHandlerKey, moveHandler)
        }
      }
    })
    
    markerElement.addEventListener('mouseleave', () => {
      const hideTimeout = setTimeout(() => {
        if (hoverPanel) {
          hoverPanel.style.display = 'none'
          this.hoverTimeouts.delete(clusterHoverKey)
          // Remove map move handler
          if (moveHandler) {
            this.map.off('move', moveHandler)
            this.moveHandlers.delete(moveHandlerKey)
            moveHandler = null
          }
        }
      }, 150)
      this.hoverTimeouts.set(clusterHoverKey, hideTimeout)
    })

    // Handle cluster click to zoom in
    markerElement.addEventListener('click', (e) => {
      e.stopPropagation()
      if (hoverPanel) {
        hoverPanel.style.display = 'none'
        // Clear timeout
        this.hoverTimeouts.delete(clusterHoverKey)
        // Remove map move handler
        if (moveHandler) {
          this.map.off('move', moveHandler)
          this.moveHandlers.delete(moveHandlerKey)
          moveHandler = null
        }
      }
      const expansionZoom = this.supercluster.getClusterExpansionZoom(cluster.properties.cluster_id)
      console.log('📍 Cluster clicked, expansion zoom:', expansionZoom, 'current zoom:', this.map.getZoom())
      
      // Ensure we zoom in at least a bit
      const targetZoom = Math.max(expansionZoom, this.map.getZoom() + 1)
      
      this.map.easeTo({
        center: [lng, lat],
        zoom: targetZoom
      })
    })

    // Create hover panel immediately (but hidden)
    createHoverPanel()
    
    // Store marker reference
    this.map._photoMarkers = this.map._photoMarkers || new Map()
    this.map._photoMarkers.set(`cluster_${cluster.properties.cluster_id}`, marker)
    
    // Clean up panel and handler when marker is removed
    ;(marker as any)._hoverPanel = hoverPanel
    ;(marker as any)._moveHandler = moveHandler
  }

  private removePhoto(photoId: string) {
    const marker = this.map._photoMarkers?.get(photoId)
    if (marker) {
      // Clean up hover panel if exists
      if ((marker as any)._hoverPanel) {
        (marker as any)._hoverPanel.remove()
      }
      marker.remove()
      this.map._photoMarkers.delete(photoId)
    }
    this.renderedPhotos.delete(photoId)
  }

  public clearMarkers() {
    this.clearAllMarkers()
  }

  public clearHoverPanels() {
    this.clearGlobalHoverPanels()
  }

  private clearAllMarkers() {
    // First clear all global hover panels and timeouts
    this.clearGlobalHoverPanels()
    
    if (this.map._photoMarkers) {
      this.map._photoMarkers.forEach((marker: any) => {
        // Clean up hover panel if exists (legacy cleanup)
        if (marker._hoverPanel) {
          marker._hoverPanel.remove()
        }
        // Clean up move handler if exists (legacy cleanup)
        if (marker._moveHandler) {
          this.map.off('move', marker._moveHandler)
        }
        marker.remove()
      })
      this.map._photoMarkers.clear()
    }
    this.renderedPhotos.clear()
  }

  private renderPhotosWithoutClustering(photos: Photo[]) {
    this.clearAllMarkers()
    photos.forEach(photo => this.renderPhoto(photo))
  }

  public highlightPhoto(photoId: string) {
    this.selectedPhotoId = photoId
    // Re-render to update highlighting
    this.updateClusters()
  }

  private clearGlobalHoverPanels() {
    // Clear all active hover panels
    this.activeHoverPanels.forEach(panel => {
      if (panel && panel.parentElement) {
        panel.remove()
      }
    })
    this.activeHoverPanels.clear()
    
    // Clear all hover timeouts
    this.hoverTimeouts.forEach(timeout => clearTimeout(timeout))
    this.hoverTimeouts.clear()
    
    // Clear all move handlers
    this.moveHandlers.forEach(handler => {
      if (this.map && handler) {
        this.map.off('move', handler)
      }
    })
    this.moveHandlers.clear()
  }



  public destroy() {
    this.clearAllMarkers()
    this.clearGlobalHoverPanels()
    
    // Remove map event listeners
    if (this.zoomHandler) {
      this.map.off('zoomend', this.zoomHandler)
      this.zoomHandler = null
    }
    if (this.moveEndHandler) {
      this.map.off('moveend', this.moveEndHandler)
      this.moveEndHandler = null
    }
  }
}